import React, { ChangeEvent, useEffect } from 'react'
import { useStore, useUnit } from 'effector-react'
import {
  CButton,
  CCardBody,
  CCol,
  CDatePicker,
  CForm,
  CFormInput,
  CFormSelect,
  CMultiSelect,
  CRow,
} from '@coreui/react-pro'
import { Divider } from 'antd'
import {
  $demoContest,
  $demoContestCreateStatus,
  setDemoContestForm,
  submitCreateDemoContest,
  submitEditDemoContest,
} from './model'
import { uniqueId } from 'lodash'
import { changedDateFormat, changedTimeFormat } from '../../../../libs/utils'
import {
  demoContestMounted,
  $demoContest as $demoContestDetails,
} from '../../details/model'
import CIcon from '@coreui/icons-react'
import { cilDelete } from '@coreui/icons'
import { transformCountriesToOptions } from '../../../../libs/formatCountries'
import { $countriesList } from '../../../../features/dictionaries/model'
import { Option } from '@coreui/react-pro/src/components/multi-select/types'
import { currencyList } from '../../../clients/widgets/account/components/CreateAccountForm/consts'

type Props = {
  mode: 'create' | 'edit'
}

const mapBonusCalculationType: { [key: string]: string } = {
  votes: 'BCT_VOTES',
  profitable_balance: 'BCT_PROFITABLE_BALANCE',
}

export const DemoContestForm: React.FC<Props> = ({ mode }) => {
  const demoContest = useStore($demoContest)
  const demoContestDetails = useStore($demoContestDetails)
  const demoContestCreateStatus = useStore($demoContestCreateStatus)
  const countriesList = useUnit($countriesList)

  useEffect(() => {
    if (mode === 'edit') {
      demoContestMounted()
    }
  }, [])

  useEffect(() => {
    if (mode === 'edit' && demoContestDetails) {
      setDemoContestForm({
        name: demoContestDetails.name,
        contestType: demoContestDetails.contestType,
        mtPlatform: demoContestDetails.mtPlatform,
        mtInitialBalance: demoContestDetails.mtInitialBalance,
        mtLeverage: String(demoContestDetails.mtLeverage),
        prizeFund: String(demoContestDetails.prizeFund),
        bonusPercent: demoContestDetails.bonusPercent,
        mtAccountGroup: demoContestDetails.mtAccountGroup,
        registrationAt: `${changedDateFormat(
          new Date(demoContestDetails.registrationAt),
        )} ${changedTimeFormat(new Date(demoContestDetails.registrationAt))}`,
        activatesAt: `${changedDateFormat(
          new Date(demoContestDetails.activatesAt),
        )} ${changedTimeFormat(new Date(demoContestDetails.activatesAt))}`,
        resultsAt: `${changedDateFormat(
          new Date(demoContestDetails.resultsAt),
        )} ${changedTimeFormat(new Date(demoContestDetails.resultsAt))}`,
        endsAt: `${changedDateFormat(
          new Date(demoContestDetails.endsAt),
        )} ${changedTimeFormat(new Date(demoContestDetails.endsAt))}`,
        currency: demoContestDetails.currency,
        //TODO - на бэке при налии пустого массив приходит [''], преобразование происходит в лисенере
        availableCountries:
          demoContestDetails.availableCountries.length === 1 &&
          demoContestDetails.availableCountries.includes('')
            ? []
            : demoContestDetails.availableCountries,
        bonusCalculationType:
          mapBonusCalculationType[demoContestDetails.bonusCalculationType] ||
          undefined,
        firstTeamAccountGroup:
          demoContestDetails.firstTeamAccountGroup || undefined,
        secondTeamAccountGroup:
          demoContestDetails.secondTeamAccountGroup || undefined,
        prizes: demoContestDetails.prizes.map((prize) => ({
          ...prize,
          amount: String(prize.amount),
          place: String(prize.place),
          id: uniqueId(),
        })),
      })
    }
  }, [demoContestDetails])

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    let value

    if (event.currentTarget.type === 'checkbox') {
      value = (event.currentTarget as HTMLInputElement).checked
    } else {
      value = event.currentTarget.value
    }

    setDemoContestForm({
      ...demoContest,
      [event.currentTarget.name]: value,
    })
  }

  const handelChangePrizes = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    id: string,
  ) => {
    let value: string = ''

    value = event.currentTarget.value

    if (
      event.currentTarget.name === 'place' ||
      event.currentTarget.name === 'amount'
    ) {
      setDemoContestForm({
        ...demoContest,
        prizes: demoContest.prizes.map((item) => {
          if (item.id === id) {
            return {
              ...item,
              [event.currentTarget.name]: value,
            }
          }
          return item
        }),
      })
    }
  }

  const handleDateChange = (date: Date | null, fieldName: string) => {
    if (date) {
      setDemoContestForm({
        ...demoContest,
        [fieldName]: `${changedDateFormat(date)} ${changedTimeFormat(date)}`,
      })
    }
  }

  const handleAddPrize = () => {
    setDemoContestForm({
      ...demoContest,
      prizes: [
        ...demoContest.prizes,
        {
          id: uniqueId(),
          place: String(demoContest.prizes.length + 1),
          amount: '',
        },
      ],
    })
  }

  const handleSubmit = () => {
    if (mode === 'create') {
      submitCreateDemoContest(demoContest)
    }
    if (mode === 'edit') {
      submitEditDemoContest({
        ...demoContest,
      })
    }
  }

  const handleDeletePrize = (id: string) => {
    setDemoContestForm({
      ...demoContest,
      prizes: demoContest.prizes
        .filter((item) => item.id !== id)
        .map((item, index) => ({ ...item, place: String(index + 1) })),
    })
  }

  const handleChangeCountries = (selected: Option[]) => {
    setDemoContestForm({
      ...demoContest,
      availableCountries: selected.map((item) => item.value as string),
    })
  }

  useEffect(() => {
    if (demoContest.contestType !== 'event') {
      setDemoContestForm({
        ...demoContest,
        bonusCalculationType: '',
      })
    }
  }, [demoContest.contestType])

  useEffect(() => {
    if (demoContest.bonusCalculationType === 'BCT_PROFITABLE_BALANCE') {
      setDemoContestForm({
        ...demoContest,
        mtAccountGroup: undefined,
      })
    } else {
      setDemoContestForm({
        ...demoContest,
        firstTeamAccountGroup: undefined,
        secondTeamAccountGroup: undefined,
      })
    }
  }, [demoContest.bonusCalculationType])

  if (demoContestCreateStatus.isSuccess && mode === 'create') {
    return (
      <CCardBody>
        Конкурс уже создан, вы можете добавить тексты и картинки на других
        вкладках
      </CCardBody>
    )
  }

  return (
    <CCardBody>
      <CForm>
        <CRow className="mb-3">
          <CCol sm={4}>
            <CFormInput
              type="text"
              name="name"
              value={demoContest.name}
              onChange={handleChange}
              label="Contest title"
              required
            />
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol sm={4}>
            <CFormSelect
              type="text"
              name="contestType"
              value={demoContest.contestType}
              onChange={handleChange}
              label="Type"
              required
            >
              <option value="">Choose type</option>
              <option value="regular">Regular</option>
              <option value="event">Event</option>
            </CFormSelect>
          </CCol>
          {demoContest.contestType === 'event' && (
            <CCol sm={4}>
              <CFormSelect
                type="text"
                name="bonusCalculationType"
                value={demoContest.bonusCalculationType}
                onChange={handleChange}
                label="Event Winner Type"
                required
              >
                <option value="">Choose type</option>
                <option value="BCT_VOTES">CRM</option>
                <option value="BCT_PROFITABLE_BALANCE">Balance</option>
              </CFormSelect>
            </CCol>
          )}
        </CRow>
        <CRow className="mb-3">
          {demoContest.bonusCalculationType === 'BCT_PROFITABLE_BALANCE' ? (
            <>
              <CCol sm={4}>
                <CFormInput
                  type="text"
                  name="firstTeamAccountGroup"
                  value={demoContest.firstTeamAccountGroup}
                  onChange={handleChange}
                  label="First team account group"
                  required
                />
                <CFormInput
                  type="text"
                  name="secondTeamAccountGroup"
                  value={demoContest.secondTeamAccountGroup}
                  onChange={handleChange}
                  label="Second team account group"
                  required
                />
              </CCol>
            </>
          ) : (
            <CCol sm={4}>
              <CFormInput
                type="text"
                name="mtAccountGroup"
                value={demoContest.mtAccountGroup}
                onChange={handleChange}
                label="Group"
              />
            </CCol>
          )}
          <CCol sm={4}>
            <CFormSelect
              type="text"
              name="mtPlatform"
              value={demoContest.mtPlatform}
              onChange={handleChange}
              label="Platform"
              required
            >
              <option value="">Choose platform</option>
              <option value="mt4">MT4</option>
              <option value="mt5">MT5</option>
            </CFormSelect>
          </CCol>
        </CRow>

        <CRow className="mb-3">
          <CCol sm={4}>
            <CMultiSelect
              label="Available countries"
              id="availableCountries"
              selectionType="text"
              virtualScroller
              options={transformCountriesToOptions(countriesList).map(
                (country) => ({
                  ...country,
                  selected: demoContest?.availableCountries?.includes(
                    country.value,
                  ),
                }),
              )}
              onChange={handleChangeCountries}
            />
          </CCol>

          <CCol sm={4}>
            <CFormSelect
              label="Currency"
              name="currency"
              placeholder={'Choose currency'}
              value={demoContest.currency}
              onChange={handleChange}
            >
              <option value="">Choose currency</option>
              {currencyList.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </CFormSelect>
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol sm={4}>
            <CFormInput
              type="text"
              name="mtInitialBalance"
              value={demoContest.mtInitialBalance}
              onChange={handleChange}
              label="Initial balance"
              minLength={1}
              required
            />
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol sm={4}>
            <CFormInput
              type="text"
              name="mtLeverage"
              value={demoContest.mtLeverage}
              onChange={handleChange}
              label="Leverage"
            />
          </CCol>
          <CCol sm={4}>
            <CFormInput
              type="text"
              name="prizeFund"
              value={demoContest.prizeFund}
              onChange={handleChange}
              label="Prize fund"
            />
          </CCol>
        </CRow>
        <CRow className="mb-3">
          <CCol sm={3}>
            <CDatePicker
              date={demoContest.registrationAt}
              timepicker
              onDateChange={(date) => handleDateChange(date, 'registrationAt')}
              label="Registration date"
            />
          </CCol>
          <CCol sm={3}>
            <CDatePicker
              date={demoContest.activatesAt}
              timepicker
              onDateChange={(date) => handleDateChange(date, 'activatesAt')}
              label="Activation date"
            />
          </CCol>
          <CCol sm={3}>
            <CDatePicker
              date={demoContest.endsAt}
              timepicker
              onDateChange={(date) => handleDateChange(date, 'endsAt')}
              label="Ended date"
            />
          </CCol>
          <CCol sm={3}>
            <CDatePicker
              date={demoContest.resultsAt}
              timepicker
              onDateChange={(date) => handleDateChange(date, 'resultsAt')}
              label="Result date"
            />
          </CCol>
        </CRow>
        {demoContest.contestType === 'event' && (
          <>
            <Divider />
            <CRow className="mb-3">
              <CCol sm={4}>
                <CFormInput
                  type="text"
                  name="bonusPercent"
                  value={demoContest.bonusPercent}
                  onChange={handleChange}
                  label="Winning bet bonus"
                />
              </CCol>
            </CRow>
          </>
        )}
        <div className="mb-3">
          <CButton size="sm" onClick={handleAddPrize}>
            + Add prizes
          </CButton>
        </div>
        {demoContest.prizes.map((prize) => {
          return (
            <CRow className="mb-3" key={prize.id}>
                <CCol sm={4}>
                  <CFormInput
                    type="text"
                    name="place"
                    value={prize.place}
                    onChange={(e) => handelChangePrizes(e, prize.id)}
                    label="Place"
                  />
                </CCol>
                <CCol sm={4}>
                  <CFormInput
                    type="text"
                    name="amount"
                    value={prize.amount}
                    onChange={(e) => handelChangePrizes(e, prize.id)}
                    label="Amount"
                  />
                </CCol>
                <CCol sm={2} className="d-flex align-items-end">
                  <CButton
                    color="danger"
                    className="me-2"
                    title={'Delete'}
                    onClick={() => handleDeletePrize(prize.id)}
                  >
                    <CIcon icon={cilDelete} />
                  </CButton>
                </CCol>
              </CRow>
          )
        })}
        <CButton className="mb-3" onClick={handleSubmit}>
          Save
        </CButton>
      </CForm>
    </CCardBody>
  )
}
